import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import FuelLoading from "../../pages/FuelLoading";
import Section from "../../components/ui/Section";
import PageHeader from "../../components/PageHeader/PageHeader";
import { useGetProductsQuery } from "../../features/api/ProductApiSlice";
import "./storeCatalog.scss";

const PRODUCTS_PER_PAGE = 6;

const CATEGORIES = [
  "All Items",
  "Small Car Essentials",
  "Sleep & Comfort",
  "Filming the Journey",
  "Safety & Tech",
  "Apparel",
  "Merchandise",
];

const isPrintifyProduct = (product) => product.fulfillmentMethod === "printify";
const isMerchandise = (product) =>
  product.productType === "branded_merchandise";

const getVariantOptions = (variant, printify) => {
  if (printify) return variant.optionValues || {};
  return {
    ...(variant.size ? { Size: variant.size } : {}),
    ...(variant.colorName ? { Color: variant.colorName } : {}),
    ...(variant.volume ? { Volume: variant.volume } : {}),
  };
};

const getOptionValues = (variant, name, printify) => {
  const value = getVariantOptions(variant, printify)[name];
  if (value == null || value === "") return [];
  if (!printify && name === "Size") {
    return String(value)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [String(value)];
};

const getAvailableVariants = (product) => {
  const variants = Array.isArray(product.variants) ? product.variants : [];
  return variants.filter((variant) =>
    isPrintifyProduct(product)
      ? variant.isAvailable === true
      : Number(variant.countInStock) > 0
  );
};

const getOptionGroups = (product) => {
  const groups = new Map();
  const printify = isPrintifyProduct(product);
  const availableVariants = getAvailableVariants(product);
  // Only display options that can actually be selected. For legacy merchandise,
  // keep the same option names as the product detail page.
  for (const variant of availableVariants) {
    for (const name of Object.keys(getVariantOptions(variant, printify))) {
      if (!groups.has(name)) groups.set(name, new Set());
      for (const value of getOptionValues(variant, name, printify)) {
        groups.get(name).add(value);
      }
    }
  }
  return Array.from(groups, ([name, values]) => ({
    name,
    values: [...values],
  }));
};

const getPriceLabel = (product) => {
  const availableVariants = getAvailableVariants(product);
  const prices = availableVariants
    .map((variant) =>
      variant.price == null ? Number(product.price) : Number(variant.price)
    )
    .filter((price) => Number.isFinite(price) && price >= 0);
  if (!prices.length) {
    const fallback = Number(product.price);
    return Number.isFinite(fallback)
      ? `$${fallback.toFixed(2)}`
      : "Price unavailable";
  }
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? `$${min.toFixed(2)}` : `From $${min.toFixed(2)}`;
};

const getCatalogStatus = (product) => {
  if (!isMerchandise(product)) return "available";
  if (isPrintifyProduct(product) && product.isPublished !== true)
    return "coming-soon";
  const variants = Array.isArray(product.variants) ? product.variants : [];
  if (isPrintifyProduct(product)) {
    return getAvailableVariants(product).length ? "available" : "sold-out";
  }
  return product.inStock === true &&
    (!variants.length || getAvailableVariants(product).length > 0)
    ? "available"
    : "sold-out";
};

export default function StoreCatalog() {
  const { data: products = [], isLoading, isError } = useGetProductsQuery();
  const [selectedCategory, setSelectedCategory] = useState("All Items");
  const [searchQuery, setSearchQuery] = useState("");
  const [storeTypeFilter, setStoreTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const filteredProducts = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    const query = searchQuery.trim().toLowerCase();
    return list.filter((prod) => {
      const matchesCategory =
        selectedCategory === "All Items" || prod.category === selectedCategory;
      const matchesType =
        storeTypeFilter === "all" ||
        (storeTypeFilter === "merch" && isMerchandise(prod)) ||
        (storeTypeFilter === "affiliate" &&
          prod.productType === "affiliate_recommendation");
      const matchesSearch =
        !query ||
        prod.name?.toLowerCase().includes(query) ||
        prod.tags?.some((tag) => String(tag).toLowerCase().includes(query));
      // DEVELOPMENT PREVIEW: unpublished Printify products are shown as Coming Soon.
      // Before public launch, enforce publication visibility on the server/API.
      return matchesCategory && matchesType && matchesSearch;
    });
  }, [products, selectedCategory, storeTypeFilter, searchQuery]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE)
  );
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PRODUCTS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(
    pageStart,
    pageStart + PRODUCTS_PER_PAGE
  );

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    document.getElementById("store-results")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  if (isLoading) {
    return (
      <div className="store-loading-slate">
        <FuelLoading text="Loading store inventories..." />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="store-error-slate">
        ⚠️ Failed to connect to product index catalog records.
      </div>
    );
  }

  return (
    <main className="store-catalog-page fuel-fade-in">
      <Section>
        <PageHeader
          title="The Mini Miles Shop"
          subtitle="A little something for the road ahead. Mini Miles apparel, small-car essentials, and gear inspired by the journey."
        />
        <div className="store-workspace-layout">
          <section className="store-filters-bar">
            <input
              type="text"
              placeholder="🔍 Search catalog items or keywords..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="store-search-field"
            />
            <div className="store-segmented-control">
              <button
                type="button"
                className={storeTypeFilter === "all" ? "active" : ""}
                onClick={() => {
                  setStoreTypeFilter("all");
                  setCurrentPage(1);
                }}
              >
                All Gear
              </button>
              <button
                type="button"
                className={storeTypeFilter === "merch" ? "active" : ""}
                onClick={() => {
                  setStoreTypeFilter("merch");
                  setCurrentPage(1);
                }}
              >
                🏪 Store Merch
              </button>
              <button
                type="button"
                className={storeTypeFilter === "affiliate" ? "active" : ""}
                onClick={() => {
                  setStoreTypeFilter("affiliate");
                  setCurrentPage(1);
                }}
              >
                🔗 Affiliate Links
              </button>
            </div>
          </section>

          <nav
            className="store-categories-tabs"
            aria-label="Filter catalog categories"
          >
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`cat-tab-btn ${
                  selectedCategory === cat ? "active" : ""
                }`}
                onClick={() => {
                  setSelectedCategory(cat);
                  setCurrentPage(1);
                }}
              >
                {cat}
              </button>
            ))}
          </nav>

          <div
            id="store-results"
            className="store-results-summary"
            aria-live="polite"
          >
            {filteredProducts.length > 0
              ? `Showing ${pageStart + 1}–${Math.min(
                  pageStart + PRODUCTS_PER_PAGE,
                  filteredProducts.length
                )} of ${filteredProducts.length} items`
              : "No matching items"}
          </div>

          {filteredProducts.length === 0 ? (
            <p className="store-empty-notice">
              No catalog items found matching your filter selection down this
              route.
            </p>
          ) : (
            <div className="store-products-grid">
              {paginatedProducts.map((product) => {
                const availableVariants = getAvailableVariants(product);
                // Match the catalog mockup to a purchasable Printify variant.
                // The linked detail page will open with that exact variant selected.
                const catalogImage = product.images?.[0] || product.image || "";
                const previewVariant = isPrintifyProduct(product)
                  ? availableVariants.find((variant) =>
                      variant.colorImages?.includes(catalogImage)
                    ) ||
                    availableVariants.find(
                      (variant) => variant.colorImages?.length
                    ) ||
                    availableVariants[0]
                  : null;
                const mainImage = isPrintifyProduct(product)
                  ? previewVariant?.colorImages?.includes(catalogImage)
                    ? catalogImage
                    : previewVariant?.colorImages?.[0] || catalogImage
                  : catalogImage ||
                    availableVariants.find(
                      (variant) => variant.colorImages?.length
                    )?.colorImages?.[0] ||
                    "";
                const detailsUrl = previewVariant?._id
                  ? `/shop/product/${product._id}?variant=${encodeURIComponent(
                      previewVariant._id
                    )}`
                  : `/shop/product/${product._id}`;
                const isMerch = isMerchandise(product);
                const status = getCatalogStatus(product);
                const optionGroups = isMerch ? getOptionGroups(product) : [];

                return (
                  <article
                    key={product._id}
                    className={`product-show-card ${
                      status === "sold-out" ? "product-show-card--sold-out" : ""
                    }`}
                  >
                    <div className="product-show-card__gallery">
                      <Link
                        to={detailsUrl}
                        className="product-card-details-anchor"
                      >
                        {mainImage && (
                          <img
                            src={mainImage}
                            alt={product.name}
                            loading="lazy"
                          />
                        )}
                      </Link>
                      {status === "sold-out" && (
                        <span className="sold-out-badge">Sold Out</span>
                      )}
                      {status === "coming-soon" && (
                        <span className="sold-out-badge">Coming Soon</span>
                      )}
                      {product.featured && (
                        <span className="featured-badge">★ Spotlight</span>
                      )}
                    </div>

                    <div className="product-show-card__body">
                      <span className="product-card-category">
                        {product.category}
                      </span>
                      <h2>{product.name}</h2>
                      <div
                        className="product-card-snippet"
                        dangerouslySetInnerHTML={{
                          __html: product.description || "",
                        }}
                      />

                      <div className="product-card-variants-tray">
                        {optionGroups.map(({ name, values }) => (
                          <div className="variant-line" key={name}>
                            <span className="label">{name}:</span>
                            <div className="chips-row">
                              {values.map((value) => (
                                <span
                                  key={`${name}-${value}`}
                                  className={`chip ${
                                    name.toLowerCase().includes("size")
                                      ? "chip--size"
                                      : name.toLowerCase().includes("color")
                                      ? "chip--color"
                                      : "chip--volume"
                                  }`}
                                >
                                  {value}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="product-card-footer-action-bar">
                        <div className="price-tag">
                          {isMerch ? (
                            <strong>{getPriceLabel(product)}</strong>
                          ) : (
                            <span className="affiliate-price-note">
                              {product.priceNote || "Varies by retailer"}
                            </span>
                          )}
                        </div>

                        {isMerch ? (
                          <Link
                            to={detailsUrl}
                            className="store-action-btn details-btn"
                          >
                            {status === "coming-soon"
                              ? "Preview Details"
                              : status === "sold-out"
                              ? "View Details"
                              : "View Details"}
                          </Link>
                        ) : (
                          <a
                            href={
                              product.affiliateLinks?.[0]?.url ||
                              "https://amazon.com"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="store-action-btn affiliate-btn"
                          >
                            Check Retailer 🔗
                          </a>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {filteredProducts.length > PRODUCTS_PER_PAGE && (
            <nav className="store-pagination" aria-label="Store catalog pages">
              <button
                type="button"
                className="store-pagination__arrow"
                onClick={() => goToPage(safePage - 1)}
                disabled={safePage === 1}
              >
                ← Previous
              </button>
              <div className="store-pagination__pages">
                {Array.from({ length: totalPages }, (_, index) => index + 1)
                  .filter(
                    (page) =>
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - safePage) <= 1
                  )
                  .reduce((items, page, index, pages) => {
                    if (index > 0 && page - pages[index - 1] > 1) {
                      items.push(
                        <span
                          key={`gap-${page}`}
                          className="store-pagination__ellipsis"
                        >
                          …
                        </span>
                      );
                    }
                    items.push(
                      <button
                        key={page}
                        type="button"
                        className={`store-pagination__page ${
                          safePage === page ? "active" : ""
                        }`}
                        aria-current={safePage === page ? "page" : undefined}
                        onClick={() => goToPage(page)}
                      >
                        {page}
                      </button>
                    );
                    return items;
                  }, [])}
              </div>
              <button
                type="button"
                className="store-pagination__arrow"
                onClick={() => goToPage(safePage + 1)}
                disabled={safePage === totalPages}
              >
                Next →
              </button>
            </nav>
          )}
        </div>
      </Section>
    </main>
  );
}
